'use client';

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

import { MemoryStorage } from './cognito_storage';

/**
 * 新パスワード設定のチャレンジを保持していない状態で完了させようとしたときの例外。
 *
 * チャレンジはログイン時に一度だけ渡されメモリ上にしか無いため、リロードや戻る/進むで
 * 失われる。Cognito 由来の例外（`InvalidPasswordException` など）と区別して
 * 「ログインからやり直してください」と案内できるよう、専用の型にしている。
 */
export class NewPasswordChallengeNotFoundError extends Error {
  constructor() {
    super('new password challenge is not available');
    this.name = 'NewPasswordChallengeNotFoundError';
  }
}

export class CognitoClient {
  // Cognito用のメモリーストレージ
  // クライアント用のなので、グローバルで定義しても問題ない
  private storage = new MemoryStorage();

  private userPool: CognitoUserPool;

  private completeNewPasswordChallengeFunc:
    | ((newPassword: string) => Promise<{
        idToken: string;
      }>)
    | null = null;

  constructor(userPoolId: string, clientId: string) {
    this.userPool = new CognitoUserPool({
      UserPoolId: userPoolId,
      ClientId: clientId,
      Storage: this.storage,
    });
  }

  private createCognitoUser = (userName: string): CognitoUser => {
    return new CognitoUser({
      Username: userName,
      Pool: this.userPool,
      Storage: this.storage,
    });
  };

  public confirmSignUp = async (email: string, code: string): Promise<void> => {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          console.error(err);
          reject(err);
          return;
        }
        console.log('call result: ' + result);

        resolve();
      });
    });
  };

  public authenticateUser = async (
    userName: string,
    password: string,
  ): Promise<
    | {
        idToken: string;
        newPasswordRequired: false;
      }
    | {
        idToken: undefined;
        newPasswordRequired: true;
      }
  > => {
    const cognitoUser = this.createCognitoUser(userName);

    const authn = new AuthenticationDetails({
      Username: userName,
      Password: password,
    });

    return new Promise((resolve, reject) => {
      try {
        cognitoUser.authenticateUser(authn, {
          onSuccess: (res: CognitoUserSession) => {
            resolve({
              idToken: res.getIdToken().getJwtToken(),
              newPasswordRequired: false,
            });
          },
          onFailure: (err: Error) => {
            console.error('failure', err);

            reject(err);
          },
          newPasswordRequired: () => {
            this.completeNewPasswordChallengeFunc = async (
              newPassword: string,
            ) => {
              const result = await this._completeNewPasswordChallenge(
                cognitoUser,
                newPassword,
              );
              // NOTE: 破棄は使い回しの防止が目的なので成功時のみに限る。失敗時にも破棄すると、
              // パスワードがポリシー違反で弾かれた直後に入れ直す動線が塞がってしまう
              this.completeNewPasswordChallengeFunc = null;

              return result;
            };
            resolve({
              idToken: undefined,
              newPasswordRequired: true,
            });
          },
        });
      } catch (err) {
        console.error('error', err);
        reject(err);
      }
    });
  };

  public forgotPassword = async (email: string): Promise<void> => {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: Error) => {
          reject(err);
        },
      });
    });
  };

  public confirmPassword = async ({
    email,
    code,
    newPassword,
  }: {
    email: string;
    code: string;
    newPassword: string;
  }): Promise<void> => {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err: Error) => {
          reject(err);
        },
      });
    });
  };

  private _completeNewPasswordChallenge = async (
    user: CognitoUser,
    newPassword: string,
  ): Promise<{
    idToken: string;
  }> => {
    return new Promise((resolve, reject) => {
      user.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: (session: CognitoUserSession) => {
            resolve({
              idToken: session.getIdToken().getJwtToken(),
            });
          },
          onFailure: (err: Error) => {
            // NOTE: 呼び出し元は例外名（InvalidPasswordException など）でメッセージを
            // 出し分けるため、ラップせず元の例外のまま返す
            console.error(
              `failed to completeNewPasswordChallenge: ${err.message}`,
            );
            reject(err);
          },
        },
      );
    });
  };

  /**
   * 新パスワード設定のチャレンジを保持しているか。
   *
   * チャレンジはメモリ上にしか無く、リロードなどで失われる。パスワードを入力し終えてから
   * 無駄だったと分かる事態を避けるため、画面側が表示時点で判定できるようにしている。
   */
  public hasNewPasswordChallenge = (): boolean =>
    this.completeNewPasswordChallengeFunc !== null;

  public completeNewPasswordChallenge = async (
    newPassword: string,
  ): Promise<{
    idToken: string;
  }> => {
    if (!this.completeNewPasswordChallengeFunc) {
      throw new NewPasswordChallengeNotFoundError();
    }

    return this.completeNewPasswordChallengeFunc(newPassword);
  };

  public getSession = async (): Promise<CognitoUserSession> => {
    const cognitoUser = this.userPool.getCurrentUser();

    return new Promise((resolve, reject) => {
      if (cognitoUser) {
        cognitoUser.getSession(
          (err: Error | null, session: CognitoUserSession | null) => {
            if (err) {
              reject(err);
              return;
            }
            if (!session) {
              reject('no session');
              return;
            }

            resolve(session);
          },
        );
      } else {
        reject(new Error('try to get session without user'));
      }
    });
  };

  public resendConfirmationCode = async (email: string): Promise<void> => {
    const cognitoUser = this.createCognitoUser(email);

    return new Promise((resolve, reject) => {
      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          console.error(err);

          reject(err);
          return;
        }
        console.log('call result: ' + result);
        resolve();
      });
    });
  };

  public changePassword = async (
    userName: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<void> => {
    const cognitoUser = this.createCognitoUser(userName);
    const authn = new AuthenticationDetails({
      Username: userName,
      Password: oldPassword,
    });
    return new Promise((resolve, reject) => {
      try {
        cognitoUser.authenticateUser(authn, {
          onSuccess: (_res: CognitoUserSession) => {
            cognitoUser.changePassword(
              oldPassword,
              newPassword,
              (err, result) => {
                if (err) {
                  console.error(err);

                  reject(err);
                  return;
                }
                console.log('call result: ' + result);
                resolve();
              },
            );
          },
          onFailure: (err: Error) => {
            console.error('failure', err);

            reject(err);
          },
        });
      } catch (err) {
        console.error('error', err);
        reject(err);
      }
    });
  };
}
